import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { Ban, Play, RotateCcw, Send, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";

import { TopToolbar } from "../layout/TopToolbar";
import type { AutomationJob } from "../types";
import { actionTypeLabel, jobStatusLabels, jobStatusStyles } from "./automationModel";
import { runAutomations } from "./runAutomations";

const StatusBadge = () => {
  const record = useRecordContext<AutomationJob>();
  if (!record) return null;
  return (
    <span
      className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${jobStatusStyles[record.status] ?? ""}`}
      title={record.last_error ?? undefined}
    >
      {jobStatusLabels[record.status] ?? record.status}
    </span>
  );
};

/** Approve (with confirm — this spends money), cancel, or retry one job. */
const JobActions = () => {
  const record = useRecordContext<AutomationJob>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!record) return null;

  const approveAndSend = async () => {
    setBusy(true);
    try {
      await dataProvider.update("automation_jobs", {
        id: record.id,
        data: { status: "approved", approved_at: new Date().toISOString() },
        previousData: record,
      });
      const run = await runAutomations([Number(record.id)]);
      const outcome = run.results.find((r) => r.id === Number(record.id));
      if (!outcome) {
        // Another drain claimed it first — don't report failure, show reality.
        notify("Job was already picked up by another run — check its status", {
          type: "info",
        });
      } else if (outcome.status === "sent") {
        notify(
          outcome.auditLogged
            ? "Order placed — logged on the prospect timeline"
            : "Order placed (timeline logging failed — see the job row)",
          { type: "success" },
        );
      } else {
        notify(`Send failed: ${outcome.error ?? "see job for details"}`, {
          type: "warning",
        });
      }
    } catch (error: unknown) {
      notify(
        `Could not send: ${error instanceof Error ? error.message : String(error)}`,
        { type: "warning" },
      );
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const cancel = async () => {
    await dataProvider.update("automation_jobs", {
      id: record.id,
      data: { status: "cancelled" },
      previousData: record,
    });
    refresh();
  };

  const canApprove =
    record.status === "pending_approval" || record.status === "failed";
  const canCancel =
    record.status === "pending_approval" ||
    record.status === "approved" ||
    record.status === "failed";

  const designID = record.action_config?.designID;

  return (
    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
      {canApprove && (
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
          title="Approve and place the mail order (spends money)"
        >
          {record.status === "failed" ? (
            <RotateCcw className="size-4" />
          ) : (
            <Send className="size-4" />
          )}
          {busy ? "Sending…" : record.status === "failed" ? "Retry" : "Approve & send"}
        </Button>
      )}
      {canCancel && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void cancel()}
          title="Cancel this job — nothing will be mailed"
        >
          <Ban className="size-4" /> Cancel
        </Button>
      )}
      <Confirm
        isOpen={confirmOpen}
        title={record.status === "failed" ? "Retry this mail order?" : "Place this mail order?"}
        content={
          `${actionTypeLabel(record.action_type)}${designID ? ` (design ${designID})` : ""} ` +
          `will be mailed via PostcardMania. This places a real order and spends money.` +
          (record.status === "failed" && record.last_error
            ? ` Last error: ${record.last_error}. If the error happened after the order left, retrying may mail a duplicate — check the PostcardMania portal first.`
            : "")
        }
        confirm="Send it"
        cancel="Not now"
        onConfirm={() => {
          setConfirmOpen(false);
          void approveAndSend();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
};

/**
 * Explicit drain of every `approved` job for this user — covers jobs
 * approved during a network hiccup (until the pg_cron sweep is enabled).
 * Deliberately a button, not an on-load effect: money-adjacent work never
 * runs invisibly.
 */
const ProcessQueuedButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const result = await runAutomations();
      if (result.claimed === 0) {
        notify("No approved jobs waiting", { type: "info" });
      } else {
        const sent = result.results.filter((r) => r.status === "sent").length;
        const failed = result.results.length - sent;
        notify(
          `Processed ${result.claimed}: ${sent} sent${failed ? `, ${failed} failed` : ""}`,
          { type: failed ? "warning" : "success" },
        );
        refresh();
      }
    } catch (error: unknown) {
      notify(
        `Drain failed: ${error instanceof Error ? error.message : String(error)}`,
        { type: "warning" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={() => void run()}
      title="Send every approved job now (does nothing if the queue is empty)"
    >
      <Play className="size-4" /> {busy ? "Processing…" : "Process queued"}
    </Button>
  );
};

export const AutomationJobList = () => {
  const translate = useTranslate();

  return (
    <List
      title={translate("crm.automations.outbox", { _: "Automations — Outbox" })}
      actions={
        <TopToolbar>
          <ProcessQueuedButton />
          <Button asChild variant="outline" size="sm">
            <Link to="/automation_rules" title="Define when jobs get created">
              <Settings2 className="size-4" /> Rules
            </Link>
          </Button>
        </TopToolbar>
      }
      perPage={50}
      sort={{ field: "created_at", order: "DESC" }}
    >
      <DataTable>
        <DataTable.Col
          source="created_at"
          label="Created"
          render={(record: AutomationJob) =>
            new Date(record.created_at).toLocaleString()
          }
        />
        <DataTable.Col
          label="Prospect"
          render={() => (
            <ReferenceField source="prospect_id" reference="prospects" link="show" />
          )}
        />
        <DataTable.Col
          source="action_type"
          label="Action"
          render={(record: AutomationJob) =>
            `${actionTypeLabel(record.action_type)}${
              record.action_config?.designID
                ? ` · design ${record.action_config.designID}`
                : ""
            }`
          }
        />
        <DataTable.Col source="to_stage" label="Triggered by stage" />
        <DataTable.Col label="Status" render={() => <StatusBadge />} />
        <DataTable.Col
          label="Order"
          render={(record: AutomationJob) => record.pcm_order_id ?? ""}
        />
        <DataTable.Col label="" render={() => <JobActions />} />
      </DataTable>
    </List>
  );
};
